import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Loader2, AlertCircle } from "lucide-react";
import { setCredentials } from "../features/auth/authSlice";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const REDIRECTS = {
  admin: "/dashboard/admin",
  instructor: "/dashboard/instructor",
  student: "/dashboard/student",
};

export default function GoogleAuthCallback() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const oauthErr = params.get("error");

    if (oauthErr) {
      setError(t("auth.googleError"));
      setTimeout(() => navigate("/auth?error=oauth_failed"), 2500);
      return;
    }

    if (!code) {
      setError(t("auth.googleNoCode"));
      setTimeout(() => navigate("/auth?error=no_code"), 2500);
      return;
    }

    // Strip code from URL immediately so it doesn't sit in history.
    window.history.replaceState({}, document.title, window.location.pathname);

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/google/exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // credentials: 'include' — обязательно, чтобы браузер ПРИНЯЛ Set-Cookie
          // от бэка (refresh-токен теперь в httpOnly cookie, а не в body).
          credentials: "include",
          body: JSON.stringify({ code }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.message || t("auth.googleError"));
        }

        const data = await res.json();
        const { accessToken, user } = data;
        // refreshToken больше не приходит в body — он уже установлен как
        // httpOnly cookie бэкендом. Проверяем только что есть access + user.
        if (!accessToken || !user) {
          throw new Error(t("auth.googleError"));
        }

        dispatch(setCredentials({ user, accessToken }));

        const role = user.roles?.[0] || "student";
        navigate(REDIRECTS[role] || "/dashboard/student", { replace: true });
      } catch (err) {
        console.error("Google exchange failed:", err);
        setError(err.message || t("auth.googleError"));
        setTimeout(() => navigate("/auth?error=exchange_failed"), 2500);
      }
    })();
  }, [dispatch, navigate, t]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
      <div className="text-center max-w-md">
        {error ? (
          <>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-950/30">
              <AlertCircle className="h-7 w-7 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t("auth.googleError")}</h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{error}</p>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-600" />
            <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("auth.googleProcessing")}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t("common.loading")}</p>
          </>
        )}
      </div>
    </div>
  );
}
